import React, { useRef, type FC } from 'react';

import { gettext } from '@eventbrite/i18n';

import { FollowButtonContext } from './context';

import { CheckChunkierIcon } from '@eventbrite/marmalade';

import { EventInteractions } from '@eventbrite/ads';
import { EVENT_INTERACTION } from '@eventbrite/event-renderer';
import { Event } from '../../../types';

import styles from './FollowButton.module.scss';

type TFollowButtonProps = {
    pageArea: string;
    className?: string;
    variant?: 'accent' | 'white';
    iconSize?:
        | 'xxsmall'
        | 'xsmall'
        | 'small'
        | 'medium'
        | 'large'
        | 'xlarge'
        | 'custom';
};

const FOLLOW_TEXT = gettext('Follow');
const FOLLOWING_TEXT = gettext('Following');

export const useFollowButton = () => {
    const followButtonProps = React.useContext(FollowButtonContext);
    return {
        isFollowing: followButtonProps?.isFollowing || false,
        shouldShowLoginModal: followButtonProps?.shouldShowLoginModal || false,
        event: followButtonProps?.event,
        onFollowClick: followButtonProps?.onFollowClick,
    };
};

export const useFollowCount = () => {
    const followButtonProps = React.useContext(FollowButtonContext);
    return followButtonProps?.followersCount;
};

export const FollowButton: FC<TFollowButtonProps> = ({
    pageArea,
    className = '',
    variant = 'accent',
    iconSize = 'small',
}) => {
    const buttonRef = useRef<HTMLButtonElement>(null);
    const { event, isFollowing, onFollowClick } = useFollowButton();
    const actionHandler = (action: PromotedActions) => {
        sendActionMetrics(action, buttonRef, event);
    };

    const icon = React.useMemo(
        () => (isFollowing ? <CheckChunkierIcon size={iconSize} /> : null),
        [isFollowing, iconSize],
    );

    const buttonClassName = React.useMemo(() => {
        const baseClass =
            variant === 'accent' && !isFollowing
                ? styles.followButton
                : styles.followButtonWhite;
        return `${baseClass} ${className}`;
    }, [variant, isFollowing, className]);

    return (
        <button
            ref={buttonRef}
            onClick={() => {
                onFollowClick?.(pageArea);
                actionHandler(
                    isFollowing
                        ? PromotedActions.unfollow
                        : PromotedActions.follow,
                );
            }}
            className={buttonClassName}
        >
            {icon} {isFollowing ? FOLLOWING_TEXT : FOLLOW_TEXT}
        </button>
    );
};

export enum PromotedActions {
    follow = EventInteractions.Follow,
    unfollow = EventInteractions.Unfollow,
}

export function sendActionMetrics(
    action: PromotedActions,
    element: React.RefObject<HTMLButtonElement>,
    event?: Event,
) {
    const interactionEvent = new CustomEvent(EVENT_INTERACTION, {
        detail: {
            id: event?.id,
            action,
            event,
        },
        bubbles: true,
    });

    element.current?.dispatchEvent(interactionEvent);
}
